import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import { dayjs, ENCAR_FEM_CAR } from '@encarpkg/shared';
import { login, loginForToken, getSnsUserProfile, getPaymentExtension, getV2PartnershipDealerAgreementTarget, getMyGarageLandingAvailable } from 'app/api';
import { deleteCookie, setCookie } from 'app/libs/cookie';
import encarUrlBuilder from 'app/libs/encarUrlBuilder';
import { getSiteTypeByEncarHostname, goLegacyPageByEncarHostname } from 'app/libs/location';
import { ACCESS_TOKEN_KEY, AUTH_PLATFORM_KEY } from 'app/libs/auth';
import useAuth from 'app/hook/common/useAuth';
import { testFullUrl } from 'app/libs/regEx';
import { deleteSnsSignOut } from 'app/libs/snsUtils';
import errors from 'app/constants/errors';
import useEnlog from 'app/hook/common/useEnlog';
import { isServer } from 'app/libs/environment';
import useSsrCookie from 'app/hook/common/useSsrCookie';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';
import { loggingException, loggingData } from 'app/libs/sentry';
import HttpClient from 'app/api/HttpClient';
import { setJoinData, putJoinData } from 'app/libs/joinStorageUtils';
import { MEMBER_BIRTH_DATE_FORMAT } from 'app/constants/date';
import { OFFICE_RELOCATION_AGREEMENT_TYPE, isInOfficeRelocationDuration } from 'app/libs/officeRelocation';

const isProd = process.env.NEXT_PUBLIC_ENV === 'prod';
const encarPcURL = process.env.REACT_APP_ENCAR_PC;
const MY_CAR_SELL_URL = `${ENCAR_FEM_CAR}/cloned?tab=sell`;

const getSellRedirectUrl = async (returnUrl) => {
	try {
		const {
			data: { canLandingToMyCar },
		} = await getMyGarageLandingAvailable();
		return canLandingToMyCar ? MY_CAR_SELL_URL : returnUrl;
	} catch {
		return returnUrl;
	}
};

export const redirectPasswordUpdate = (id, isInvalidPattern, backPath, returnUrl, shouldCheckSellRedirect) => {
	if (isServer) return;
	const patternCheck = isInvalidPattern ? 'Y' : 'N';
	// pc | mobile | null
	const siteType = getSiteTypeByEncarHostname();

	const pcPathname = `/co/login.do?${new URLSearchParams({
		method: 'goChangePw',
		backpath: encarPcURL,
		pageid: 'co_changepassword',
		patternChk: patternCheck,
		userId: id,
		...(shouldCheckSellRedirect && { shouldCheckSellRedirect: 'true' }),
	})}`;

	const mobileParams = new URLSearchParams({
		method: 'goChangePw',
		pageid: 'my_pwchange_i01',
		patternChk: patternCheck,
		userid: id,
		...(shouldCheckSellRedirect && { shouldCheckSellRedirect: 'true' }),
	});

	if (backPath) {
		mobileParams.set('backpath', encodeURIComponent(backPath));
	} else if (returnUrl) {
		mobileParams.set('returnUrl', returnUrl);
	} else {
		mobileParams.set('backpath', '/my/my_index.do');
	}

	const mobilePathname = `/co/login.do?${mobileParams}`;

	if (siteType == null) {
		window.location.href = encarUrlBuilder().pc(pcPathname).mobile(mobilePathname);
	}
	if (siteType === 'pc') {
		goLegacyPageByEncarHostname(pcPathname);
	}
	if (siteType === 'mobile') {
		goLegacyPageByEncarHostname(mobilePathname);
	}
};

export const setAuthInfo = (sessionKey, accessToken) => {
	setCookie('X-Auth-Token-Encar', sessionKey);
	setCookie(ACCESS_TOKEN_KEY, accessToken);
};

const USER_TYPE_BY_GRADE = {
	REPEAT: 0,
	INDIVIDUAL: 1,
	DEALER: 2,
};

export const setLegacyInfo = (id, euckrEncodedName, grade) => {
	setCookie('USERID', encodeURI(id));
	setCookie('USERNAME', euckrEncodedName);
	setCookie('USERTYPE', USER_TYPE_BY_GRADE[grade]);
	setCookie('PERSISTENT_USERTYPE', USER_TYPE_BY_GRADE[grade], 3650);
};

function useLogin() {
	const history = useHistory();
	const { getUser, isLoggedIn } = useAuth();
	const { enloggingJsonData } = useEnlog();
	const location = useLocation();
	const ua = useSsrUserAgent();
	const { getCookie } = useSsrCookie();

	const goEncarRedirect = useCallback((url) => {
		try {
			const decodedUrl = decodeURIComponent(url);
			if (testFullUrl(decodedUrl)) {
				window.location.replace(decodedUrl);
			} else {
				goLegacyPageByEncarHostname(decodedUrl);
			}
		} catch (e) {
			goLegacyPageByEncarHostname('/index.do');
		}
	}, []);

	const goFemRedirect = useCallback((pathname) => {
		window.location.replace(pathname);
	}, []);

	const redirectSnsJoin = useCallback(
		async (requestData) => {
			if (!requestData) {
				return;
			}
			try {
				/** 플랫폼에서 snsCallbackPage의 쿼리 파람으로 전달된 토큰값과 platform 값 { token, platform } */
				const { platform, token } = requestData;
				const { data } = await getSnsUserProfile(platform, token);
				const defaultForm = {};
				if (data.name) defaultForm.name = data.name;
				if (data.email) defaultForm.email = data.email;
				if (data.phone) {
					defaultForm.certificationKey = 'certified';
					defaultForm.phone = data.phone;
				}
				if (data.birth) defaultForm.birth = dayjs(data.birth).format(MEMBER_BIRTH_DATE_FORMAT);
				if (data.gender) defaultForm.gender = data.gender;

				setJoinData({ social: { token, platform }, form: defaultForm });

				/** 회원가입이 필요한 카카오/네이버 로그인 사용자 중 */
				if (['KAKAO', 'NAVER'].includes(data.site)) {
					/** 이름/전화번호/생년월일/성별 정보 제공 여부 */
					const hasRequiredUserInfo = data.name && data.phone && data.birth && data.gender;

					if (hasRequiredUserInfo) {
						history.replace('/join/sns/duplicate');
					} else {
						// form 페이지에서 agreements가 없다면 /login 페이지로 리다이렉트 처리를 피하기 위함
						putJoinData('agreements', []);
						history.replace('/join/sns/form');
					}
					return;
				}
				/** 구글/애플의 경우 약관(agreements) 정보를 제공하지 않기 때문에 약관 동의 페이지로 이동 */
				history.replace('/join/sns/terms');
			} catch (e) {
				alert('SNS 정보 조회에 실패했습니다.');
			}
		},
		[history],
	);

	/**
	 * err: loginForToken API (`/user/sign-in/${platform}`) 호출시 발생하는 에러
	 * requestData: 플랫폼에서 snsCallbackPage의 쿼리 파람으로 전달된 토큰값과 platform 값 { token, platform }
	 */
	const handleErrorRedirect = useCallback(
		(err, requestData) => {
			const { status, headers = {}, data = {} } = err.response || {};

			if (data && data.code === errors.Code.User.WITHDRAWAL_USER) {
				const { link } = headers;
				window.location.href = link;
				return false;
			}

			if (data && data.code === errors.Code.User.BLACK_LIST_USER) {
				const { link } = headers;
				window.location.href = link;
				return false;
			}

			// 회원가입 해야하는 케이스
			if (status === 404 && data.code === errors.Code.User.NOT_FOUND_USER) {
				redirectSnsJoin(requestData);
				return false;
			}

			console.error(err);
			throw err;
		},
		[redirectSnsJoin],
	);

	const handlePopupModeLoginSuccess = useCallback(() => {
		window.close();
	}, []);

	const postSuccessMessageToParent = useCallback(() => {
		if (window.opener && !window.opener.closed) {
			window.opener.postMessage({ type: 'login_success' }, '*');
		} else if (window.parent !== window) {
			window.parent.postMessage({ type: 'login_success' }, '*');
		}
	}, []);

	const checkExtensionTarget = useCallback(async (id) => {
		const start = isProd ? '2025-10-30T10:00:00' : '2025-10-28T10:00:00';
		const end = '2025-11-30T18:00:00';
		const inDuration = dayjs().isAfter(start) && dayjs().isBefore(end);
		if (!inDuration) {
			return {
				isExtensionTarget: false,
				hasExtensionAgreement: null,
			};
		}
		const {
			data: { isExtensionTarget, hasExtensionAgreement },
		} = await getPaymentExtension(id);
		return {
			isExtensionTarget,
			hasExtensionAgreement,
		};
	}, []);

	/**
	 * 본사 이전 주소 확인 안내 대상자 여부
	 * - 노출 기간 밖이면 조회하지 않음
	 * - 404(제휴 딜러 아님) / targetFlag 가 true 가 아니면 대상 아님
	 * - 기동의 여부(agreementFlag)는 서버의 targetFlag 판정에 이미 반영됨
	 * - 조회 실패가 로그인 흐름을 막지 않도록 예외는 삼킨다
	 */
	const checkOfficeRelocationTarget = useCallback(async (dealerId) => {
		if (!isInOfficeRelocationDuration()) return false;
		try {
			const { data } = await getV2PartnershipDealerAgreementTarget({
				dealerId,
				agreementType: OFFICE_RELOCATION_AGREEMENT_TYPE,
			});
			return data?.targetFlag === true;
		} catch (e) {
			return false;
		}
	}, []);

	const loginBasic = useCallback(
		async (id, password, captcha, options) => {
			try {
				const { data } = await login(id, password, captcha);
				const { sessionKey, apiKey, basicAuthValidity = {} } = data || {};
				const { from, to } = basicAuthValidity;
				const isOldPassword = !from;
				const isExpiredPassword = to && dayjs(to).isSameOrBefore(dayjs());
				const { noRedirect, backPath, returnUrl, saveId, popupMode, shouldCheckSellRedirect } = options || {};
				const { isExtensionTarget, hasExtensionAgreement } = await checkExtensionTarget(data.user.id);
				const isOfficeRelocationTarget = await checkOfficeRelocationTarget(data.user.id);
				setAuthInfo(sessionKey, apiKey.accessToken);
				setLegacyInfo(data.user.id, data.user.eucKrEncodedName, data.user.grade);
				setCookie(AUTH_PLATFORM_KEY, 'ENCAR');
				await getUser({
					checkAuth: true,
					passAuth: true,
				});

				if (saveId) {
					setCookie('ENID_LOGIN_ID', id, 365);
				} else {
					deleteCookie('ENID_LOGIN_ID');
				}

				// pc에서 사용하는 쿠키
				deleteCookie('marketing_terms_click');

				const dataTalkCustom = {
					eventtype: 'ACTION',
					eventname: '로그인',
					param: {
						loginmethod: 'ENCAR',
					},
				};
				enloggingJsonData({ dataTalkCustom });
				postSuccessMessageToParent();
				if (!noRedirect) {
					// 정기결제 대상자이고 동의하지 않았다면 동의 페이지로 이동
					const isGoToAgreePage = isExtensionTarget && hasExtensionAgreement === null;
					if (isGoToAgreePage || returnUrl?.indexOf('updateagree') > -1) {
						const passwordUpdateReturnUrl = isExpiredPassword || isOldPassword;
						const searchInfo = qs.stringify({
							passwordUpdateReturnUrl,
							backpath: backPath,
							returnUrl,
						});
						const searchParam = searchInfo ? `?${searchInfo}` : '';
						goFemRedirect(`/landing/update-agree${searchParam}`);
						return true;
					}

					// 본사 이전 주소 확인 대상자라면 안내 페이지로 이동
					if (isOfficeRelocationTarget || returnUrl?.indexOf('officerelocation') > -1) {
						const passwordUpdateReturnUrl = isExpiredPassword || isOldPassword;
						const searchInfo = qs.stringify({
							passwordUpdateReturnUrl,
							backpath: backPath,
							returnUrl,
						});
						const searchParam = searchInfo ? `?${searchInfo}` : '';
						goFemRedirect(`/landing/office-relocation${searchParam}`);
						return true;
					}

					// 비밀번호 변경이 필요한 회원
					if (isExpiredPassword || isOldPassword) {
						redirectPasswordUpdate(id, !!isOldPassword, backPath, returnUrl, shouldCheckSellRedirect);
					} else if (popupMode === 'true' || popupMode === true) {
						handlePopupModeLoginSuccess();
					} else if (backPath) {
						goEncarRedirect(backPath);
					} else if (returnUrl) {
						const moveUrl = shouldCheckSellRedirect ? await getSellRedirectUrl(returnUrl) : returnUrl;
						goFemRedirect(moveUrl);
					} else {
						goLegacyPageByEncarHostname('/index.do');
					}
				}
				return true;
			} catch (e) {
				if (e.response?.status >= 500) {
					loggingData(`로그인인증(엔카) [${e.response?.status}]`, e);
				}
				return handleErrorRedirect(e);
			}
		},
		[
			checkExtensionTarget,
			checkOfficeRelocationTarget,
			getUser,
			enloggingJsonData,
			postSuccessMessageToParent,
			goFemRedirect,
			handlePopupModeLoginSuccess,
			goEncarRedirect,
			handleErrorRedirect,
		],
	);

	const loginSns = useCallback(
		async (token, platform, options) => {
			try {
				const { data } = await loginForToken(token, platform);
				const { sessionKey, apiKey } = data || {};
				const { noRedirect, backPath, returnUrl, popupMode, shouldCheckSellRedirect } = options || {};

				setAuthInfo(sessionKey, apiKey.accessToken);
				setLegacyInfo(data.user.id, data.user.eucKrEncodedName, data.user.grade);
				const { isExtensionTarget, hasExtensionAgreement } = await checkExtensionTarget(data.user.id);
				const isOfficeRelocationTarget = await checkOfficeRelocationTarget(data.user.id);
				setCookie(AUTH_PLATFORM_KEY, platform);
				await getUser({
					checkAuth: true,
					passAuth: true,
				});
				deleteSnsSignOut(platform);

				// pc에서 사용하는 쿠키
				deleteCookie('marketing_terms_click');

				const dataTalkCustom = {
					screenname: '로그인',
					eventtype: 'ACTION',
					eventname: '로그인',
					param: {
						loginmethod: platform,
					},
				};
				enloggingJsonData({ dataTalkCustom });
				postSuccessMessageToParent();

				if (!noRedirect) {
					// 정기결제 대상자이고 동의하지 않았다면 동의 페이지로 이동
					const isGoToAgreePage = isExtensionTarget && hasExtensionAgreement === null;
					if (isGoToAgreePage || returnUrl?.indexOf('updateagree') > -1) {
						const searchInfo = qs.stringify({
							backpath: backPath,
							returnUrl,
						});
						const searchParam = searchInfo ? `?${searchInfo}` : '';
						goFemRedirect(`/landing/update-agree${searchParam}`);
						return true;
					}

					// 본사 이전 주소 확인 대상자라면 안내 페이지로 이동
					if (isOfficeRelocationTarget || returnUrl?.indexOf('officerelocation') > -1) {
						const searchInfo = qs.stringify({
							backpath: backPath,
							returnUrl,
						});
						const searchParam = searchInfo ? `?${searchInfo}` : '';
						goFemRedirect(`/landing/office-relocation${searchParam}`);
						return true;
					}

					if (popupMode === 'true' || popupMode === true) {
						handlePopupModeLoginSuccess();
					} else if (backPath) {
						goEncarRedirect(backPath);
					} else if (returnUrl) {
						const moveUrl = shouldCheckSellRedirect ? await getSellRedirectUrl(returnUrl) : returnUrl;
						goFemRedirect(moveUrl);
					} else {
						goLegacyPageByEncarHostname('/index.do');
					}
				}
				return true;
			} catch (e) {
				const httpStatus = e.response?.status;
				const responseCode = e.response?.data?.code;
				// 회원가입이 필요한 케이스(404)는 제외
				if (httpStatus !== 404) {
					const codeInfo = responseCode ? `(code:${responseCode})` : '';
					loggingData(`로그인인증(소셜-엔카) ${platform}[${httpStatus}]${codeInfo}`, e);
				}
				return handleErrorRedirect(e, { token, platform });
			}
		},
		[
			checkExtensionTarget,
			checkOfficeRelocationTarget,
			getUser,
			enloggingJsonData,
			postSuccessMessageToParent,
			goFemRedirect,
			handlePopupModeLoginSuccess,
			goEncarRedirect,
			handleErrorRedirect,
		],
	);

	const loginedRedirect = useCallback(
		(isRedirectActiveSessionUser = false) => {
			if (!ua().isApp) {
				const { backpath, returnUrl } = qs.parse(location.search) || {};
				const loginedMove = () => {
					if (backpath) {
						goEncarRedirect(backpath);
					} else if (returnUrl) {
						goFemRedirect(returnUrl);
					} else {
						goLegacyPageByEncarHostname('/index.do');
					}
				};

				if (isLoggedIn) {
					loginedMove();
					return;
				}

				if (isRedirectActiveSessionUser) {
					const authSession = getCookie('X-Auth-Token-Encar');
					const authToken = getCookie(ACCESS_TOKEN_KEY);

					if (authSession && authToken) {
						(async () => {
							try {
								const res = await HttpClient.authentication();
								if (res && res.status === 200) {
									loginedMove();
								}
							} catch (e) {
								console.error(e);
								loggingException(e);
							}
						})();
					}
				}
			}
		},
		[isLoggedIn, goEncarRedirect, goFemRedirect, location.search, ua, getCookie],
	);

	return {
		loginBasic,
		loginSns,
		loginedRedirect,
	};
}

export default useLogin;
